import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { google_drive, uplode_file } from "../../assets/images/images";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../../features/auth/AuthContext";
import {
  submitGoogleToken,
  resetsubmitgoogleToken,
} from "../../state/actions/submitGoogleTokenAction";
import {
  getGoogleTokenAction,
  resetGoogleToken,
} from "../../state/actions/getGoogleTokenAction";
import { createEmbeddingsAPI } from "../../state/actions/createEmbeddingsAction";
import { GoogleDriveIcon } from "../FileRepo/Icons";

const GoogleDriveUpload = ({
  setShowUploadPop,
  handleFileChange,
  setFile_list_id,
}) => {
  const googleAccessToken = useSelector((state) => state.getGoogleAccessToken);
  const [accessToken, setAccessToken] = useState(
    googleAccessToken?.googleToken?.google_token?.token,
  );
  const [pickerInited, setPickerInited] = useState(false);
  const [statusCode, setStatusCode] = useState(null);
  const fileInputRef = useRef(null);
  const scrollListenerRef = useRef(null);

  const attachScrollGuard = (savedScrollY) => {
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current);
    }
    const handler = () => {
      window.scrollTo({ top: savedScrollY, left: 0 });
    };
    scrollListenerRef.current = handler;
    window.addEventListener("scroll", handler);
  };

  const detachScrollGuard = () => {
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current);
      scrollListenerRef.current = null;
    }
  };

  const dispatch = useDispatch();
  const { token } = useAuth();

  const googleToken = useSelector((state) => state.submitGoogleToken);

  // Function to handle token expiration and refresh if needed
  const handleTokenExpiration = async () => {
    const currentTime = Date.now();
    const expiryTime = new Date(
      googleAccessToken?.googleToken?.google_token?.expiry,
    ).getTime(); // Get the token expiry time from localStorage or state

    // Check if the token has expired
    if (currentTime > expiryTime) {
      const refreshToken =
        googleAccessToken?.googleToken?.google_token?.refresh_token; // Get the refresh token from localStorage or state

      if (!refreshToken) {
        return;
      }

      try {
        // Request a new access token using the refresh token
        const response = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
            client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
            refresh_token: refreshToken,
            grant_type: "refresh_token",
          }),
        });

        const data = await response.json();

        if (data.error) {
          return;
        }

        // Store the new access token and update the expiry time
        const newAccessToken = data.access_token;
        const newExpiryTime = Date.now() + data.expires_in * 1000; // Calculate new expiry time

        // Update localStorage or Redux store with the new token and expiry time
        localStorage.setItem("access_token", newAccessToken);
        localStorage.setItem("token_expiry", newExpiryTime); // Store the new expiry time
        localStorage.setItem(
          "refresh_token",
          data.refresh_token || refreshToken,
        ); // Store new refresh token if available

        // Update the Google token
        dispatch(
          submitGoogleToken(
            {
              google_token: {
                token: newAccessToken,
                refresh_token: data.refresh_token || refreshToken,
                token_uri: "https://oauth2.googleapis.com/token",
                client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
                client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
                // scopes: tokenResponse.scope ? data.scope.split(' ') : [],
                scopes: data.scope ? data.scope.split(" ") : [], // Use data.scope here
                expiry: new Date(newExpiryTime).toISOString(),
              },
            },
            token,
          ),
        );

        return newAccessToken; // Return the new access token
      } catch (error) {
        return null;
      }
    } else {
      return accessToken; // Token is still valid, return the existing token
    }
  };

  // Create and display the Google Picker
  const createPicker = (accToken) => {
    if (!accToken) {
      return;
    }

    const picker = new window.google.picker.PickerBuilder()
      .addView(window.google.picker.ViewId.DOCS)
      .setOAuthToken(accToken)
      .setDeveloperKey(process.env.REACT_APP_GOOGLEDRIVE_API_KEY) // Replace with your API key
      .setCallback((data) => pickerCallback(data, accToken))
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .build();

    const savedScrollY = window.scrollY;
    attachScrollGuard(savedScrollY);
    picker.setVisible(true);
  };

  const pickerCallback = async (data, accToken) => {
    if (data.action === window.google.picker.Action.PICKED) {
      detachScrollGuard();
      // Append selected file IDs directly into array state
      try {
        const selectedIds = (data.docs || [])
          .map((doc) => doc.id)
          .filter(Boolean);
        if (selectedIds.length && typeof setFile_list_id === "function") {
          setFile_list_id((prev) => [...prev, ...selectedIds]);
        }
      } catch (e) {}

      const files = await Promise.all(
        data.docs.map(async (doc) => {
          try {
            let downloadUrl;
            if (doc.mimeType === "application/vnd.google-apps.spreadsheet") {
              // Export Google Spreadsheet as XLSX
              downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
              doc.mimeType = "application/xlsx";
              doc.name = `${doc.name}.xlsx`;
            } else if (
              doc.mimeType === "application/vnd.google-apps.document"
            ) {
              // Export Google Docs as DOCX
              downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=application/vnd.openxmlformats-officedocument.wordprocessingml.document`;
              doc.mimeType = "application/docx";
              doc.name = `${doc.name}.docx`;
            } else {
              // For other file types, download the original file
              downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`;
            }

            const response = await fetch(downloadUrl, {
              headers: new Headers({
                Authorization: `Bearer ${accToken}`,
              }),
            });

            if (!response.ok) {
              throw new Error(
                `Failed to fetch file: ${doc.name}, Status: ${response.status}`,
              );
            }

            const blob = await response.blob();

            // Validate blob size and file integrity
            if (blob.size === 0) {
              toast.error(`${doc.name} seems corrupted.`);
              return null;
            }

            const mimeType = doc.mimeType || blob.type;
            // Dispatch API call for each valid doc.id after validation
            dispatch(createEmbeddingsAPI(doc.id, token));
            // Return the constructed File object
            return new File([blob], doc.name, {
              type: mimeType,
              lastModified: new Date(doc.lastEditedUtc).getTime(),
            });
          } catch (error) {
            console.error("Error fetching file:", error);
            return null;
          }
        }),
      );

      const failedFiles = files.some((file) => file === null);

      if (failedFiles) {
        detachScrollGuard();
        if (statusCode === 403) {
          toast.error("File/s too large to upload.");
          return;
        } else {
          toast.error("Failed to upload file/s.");
        }
        return;
      }

      const filteredFiles = files.filter((file) => file !== null);

      const dataTransfer = new DataTransfer();
      filteredFiles.forEach((file) => dataTransfer.items.add(file));
      const fileList = dataTransfer.files;

      const event = new Event("change", { bubbles: true });
      Object.defineProperty(event, "target", {
        writable: true,
        value: { files: fileList },
      });
      setStatusCode(null);
      handleFileChange(event);
    } else if (data.action === "cancel") {
      detachScrollGuard();
    }
  };

  // Request an access token if not already available
  const handleOpenPicker = async () => {
    const validToken = await handleTokenExpiration(); // Ensures the token is valid

    if (!validToken) {
      loginWithGoogle();
    } else {
      createPicker(validToken);
      // createPicker(accessToken);
    }
    setShowUploadPop(false);
  };

  const onSuccess = async (tokenResponse) => {
    const response = await fetch("https://oauth2.googleapis.com/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        code: tokenResponse.code,
        client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
        client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
        redirect_uri: process.env.REACT_APP_SHARE_URL.replace(/\/$/, ""),
        // redirect_uri: 'http://localhost:3000',
        // redirect_uri: process.env.REACT_APP_SHARE_URL, // Ensure this matches with your console setup
        grant_type: "authorization_code",
        access_type: "offline",
        include_granted_scopes: true,
      }),
    });

    const tokenData = await response.json();

    if (tokenData) {
      const creds = {
        google_token: {
          token: tokenData.access_token,
          refresh_token: tokenData.refresh_token || null,
          token_uri: "https://oauth2.googleapis.com/token",
          client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
          client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
          scopes: tokenResponse.scope ? tokenData.scope.split(" ") : [],
          expiry: new Date(
            Date.now() + tokenData.expires_in * 1000,
          ).toISOString(),
        },
      };

      dispatch(submitGoogleToken(creds, token));
      setAccessToken(tokenData?.access_token);
      createPicker(tokenData?.access_token);
    }
  };

  const onFailure = (error) => {
    console.error("Login failed:", error);
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess,
    onError: onFailure, // Changed from onFailure to onError as per the GIS library
    scope: [
      "https://www.googleapis.com/auth/drive.file",
      "https://www.googleapis.com/auth/drive",
      "https://www.googleapis.com/auth/drive.metadata.readonly",
      "https://www.googleapis.com/auth/drive.metadata",
    ].join(" "),
    access_type: "offline",
    approval_prompt: "force",
    prompt: "consent",
    flow: "auth-code",
  });

  useEffect(() => {
    const loadGoogleScripts = () => {
      const apiScript = document.createElement("script");
      apiScript.src = "https://apis.google.com/js/api.js"; // Load Google APIs
      apiScript.async = true;
      apiScript.onload = () => {
        window.gapi.load("picker", () => {
          setPickerInited(true); // Set pickerInited to true when Picker API is loaded
        });
      };
      document.body.appendChild(apiScript);
    };

    loadGoogleScripts();
  }, []);

  useEffect(() => {
    if (googleToken?.google_token !== null) {
      dispatch(getGoogleTokenAction(token));
    }
  }, [googleToken]);

  useEffect(() => {
    if (googleAccessToken?.googleToken) {
      setAccessToken(googleAccessToken?.googleToken?.google_token?.token);
    }
  }, [googleAccessToken]);

  useEffect(() => {
    return () => {
      detachScrollGuard();
      dispatch(resetsubmitgoogleToken());
    };
  }, []);

  return (
    <>
      <div
        className="google-drive-popup-folder"
        onClick={handleOpenPicker}
        disabled={!pickerInited}
        style={{ cursor: "pointer" }}
      >
        <input type="file" ref={fileInputRef} style={{ display: "none" }} />
        <div className="google-drive-image">
          {/* <img src={google_drive} alt="Google Drive" /> */}
          <GoogleDriveIcon />
        </div>
        <p>
          {accessToken === undefined ? "Connect To Drive" : "Upload from Google Drive"}
        </p>
      </div>
    </>
  );
};

export default GoogleDriveUpload;
